<script setup lang="ts">
import { useWinnersData } from '~/composables/useStates/useWinnersData';
import Select from 'primevue/select';
import type {
  ISortByOption,
  TMyBigWinsSortField,
  TSortByValue,
} from '~/interfaces/winners/my-big-wins';

const { $t } = useNuxtApp();
const {
  loadMyBigWins,
  bigWinsSortOrder,
  bigWinsSortField,
  bigWinsFilterBy,
  bigWinsFilterOptions,
} = useWinnersData();

const filterOption = ref<string>(null);
const sortOption = ref<TSortByValue>(null);
const container = ref<HTMLDivElement>();
const containerWidth = computed(() => `${container.value?.clientWidth}`);

const limit = inject<Ref<number>>('myBigWinsLimit');

const options = computed(() =>
  bigWinsFilterOptions.value.map((option) => ({
    label: option,
    value: option,
  })),
);

const sortOptions: ISortByOption[] = [
  { label: $t('wins-high-low'), value: 'WinAmount Desc' },
  { label: $t('wins-low-high'), value: 'WinAmount Asc' },
  { label: $t('wager-high-low'), value: 'WagerAmount Desc' },
  { label: $t('wager-low-high'), value: 'WagerAmount Asc' },
  { label: $t('newest'), value: 'WinDate Desc' },
  { label: $t('oldest'), value: 'WinDate Asc' },
];

const setDefaults = () => {
  filterOption.value = null;
  bigWinsFilterBy.value = null;
  bigWinsSortField.value = 'WinAmount';
  bigWinsSortOrder.value = 'Desc';
};

const handleReload = (reset: boolean = false) => {
  if (reset) setDefaults();

  loadMyBigWins({
    limit: limit.value,
    skip: 0,
    sortOrder: bigWinsSortOrder.value,
    sortField: bigWinsSortField.value,
    ...(!!bigWinsFilterBy.value && { filter: bigWinsFilterBy.value }),
    resetGameNames: false,
  });
};

watch(
  () => [filterOption.value, sortOption.value],
  ([newFilterOption, newSortOption]) => {
    bigWinsFilterBy.value = newFilterOption;
    const [sortOption, sortBy] = newSortOption.split(' ');

    bigWinsSortField.value = sortOption as TMyBigWinsSortField;
    bigWinsSortOrder.value = sortBy as 'Asc' | 'Desc';

    if (filterOption.value === $t('see-all')) handleReload(true);
    handleReload();
  },
);

onUnmounted(setDefaults);

onMounted(
  () =>
    (sortOption.value = `${bigWinsSortField.value} ${bigWinsSortOrder.value}`),
);
</script>

<template>
  <LazyUserAccountSettingsHeader :title="$t('my-big-wins')">
    <template #actions>
      <div class="flex gap-2 w-full items-center justify-center">
        <div ref="container" class="w-full relative">
          <Select
            v-model="filterOption"
            :options="options"
            :placeholder="$t('sort-by-game')"
            option-label="label"
            label-class="capitalize"
            option-value="value"
            class="w-full"
            append-to="body"
            :width="containerWidth"
            :pt:label="'!py-0.5 text-base-priority w-full truncate text-ellipsis px-3'"
            :pt:option-label="'capitalize'"
          />
        </div>
        <div class="w-full relative">
          <Select
            v-model="sortOption"
            :options="sortOptions"
            :placeholder="$t('sort-by-type')"
            option-label="label"
            label-class="capitalize"
            option-value="value"
            class="w-full"
            append-to="body"
            :width="containerWidth"
            :pt:label="'!py-0.5 text-base-priority w-full truncate text-ellipsis px-3'"
            :pt:option-label="'capitalize'"
          />
        </div>
      </div>
    </template>
  </LazyUserAccountSettingsHeader>
</template>
